import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { envVariables } from '@fabric-ui/utils';
import { CodeOwner } from '@utils/router.utils';
import kfuseRumSDK from 'kf-browser-sdk';
import { getExporterWorkflowMetadata, isExportMode } from './exportDashboards.utils';

export const isSynthetics =
  window._DATADOG_SYNTHETICS_BROWSER || // eslint-disable-line no-underscore-dangle
  navigator.userAgent.includes('Datadog') ||
  navigator.userAgent.includes('checklyhq.com');

export const isDebugMode = () => {
  const resetDebugModeCount = Number(localStorage.getItem('resetDebugModeCount'));
  if (!['prod-se01', 'prod'].includes(envVariables.VITE_ENV) || resetDebugModeCount > 0) {
    return localStorage.getItem('debugMode') === 'true';
  }
  localStorage.setItem('debugMode', 'false');
  localStorage.setItem('resetDebugModeCount', `${resetDebugModeCount + 1}`);
  return false;
};
export const shouldRecordSession = !isDebugMode() && !isSynthetics && !envVariables.STORYBOOK && envVariables.VITE_DD_CLIENT_TOKEN;

const getBody = (context: any) => {
  try {
    return context.requestInit?.body && typeof context.requestInit.body === 'string' && JSON.parse(context.requestInit.body);
  } catch (e) {
    console.warn(e, context.requestInit?.body);
    return '<body failed to parse>';
  }
};

const getOwnerByErrorMessage = ({
  message,
  handling_stack: stackTrace,
}: {
  message: string;
  handling_stack?: string;
}): CodeOwner | undefined => {
  const isQueryError = ['queryObjectAsync', 'queryObject', 'querySql'].some(term => message.includes(term));

  if ((message.includes('Effect failure') || message.includes('Failed to fetch')) && isQueryError) {
    return CodeOwner.Query;
  }
  if (stackTrace?.includes('pendo.js') || message.includes('[Pendo]')) {
    return CodeOwner.Pendo;
  }
  if (message.includes('[debugging enabled]')) {
    return CodeOwner.Debugging;
  }
  if (message.includes('Uncaught "Empty reason"') || message.includes('[Context Error]')) {
    return CodeOwner.Frontend;
  }
  return undefined;
};

const getOwnerFromStaticRoute = ({ matches }): CodeOwner | undefined =>
  matches.findLast(({ staticData }) => staticData?.owner)?.staticData.owner;

export const initRum = router => {
  if (shouldRecordSession) {
    datadogRum.init({
      applicationId: '79576ecf-b1bc-4bf0-ab5f-a99515bd4b2c',
      clientToken: envVariables.VITE_DD_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'app-avalor',
      env: envVariables.VITE_ENV,
      version: envVariables.VITE_VERSION,
      sessionReplaySampleRate: 100,
      compressIntakeRequests: true,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'allow',
      plugins: [reactPlugin({ router: false })],
      beforeSend: (event, context: any) => {
        // eslint-disable-next-line no-param-reassign
        event.context = {
          ...event.context,
          owner:
            (event.type === 'error' && getOwnerByErrorMessage(event.error)) || getOwnerFromStaticRoute(router.state) || CodeOwner.Frontend,
          ...(event.type === 'resource' && event.resource.type === 'fetch'
            ? {
                request: {
                  graphqlname: context.requestInit?.headers?.graphqlname,
                  body: getBody(context),
                },
                response: { headers: Object.fromEntries(context.response?.headers || []) },
              }
            : {}),
        };
        return true;
      },
    });

    // Inject KFuse RUM
    if (envVariables.VITE_KFUSE_APPLICATION_ID) {
      kfuseRumSDK.init({
        config: {
          applicationId: envVariables.VITE_KFUSE_APPLICATION_ID,
          clientToken: envVariables.VITE_KFUSE_CLIENT,
          proxy: envVariables.VITE_KFUSE_URL,
          service: 'app-avalor',
          env: envVariables.VITE_ENV,
          version: envVariables.VITE_VERSION,
          sessionSampleRate: 100,
          defaultPrivacyLevel: 'allow',
          enableSessionRecording: true,
          enableLogCollection: true,
        },
      });
    }

    datadogRum.startSessionReplayRecording();
    if (isExportMode()) {
      datadogRum.setUser({
        ...datadogRum.getUser(),
        ...getExporterWorkflowMetadata(),
      });

      kfuseRumSDK.setUser({
        ...datadogRum.getUser(),
        ...getExporterWorkflowMetadata(),
      });
    }
  }
};
